<template>
    <div>
        <breadcrumbs :page="$t('helpers.my_selection')"/>
        <div class="favorites">
            <div class="container">
                <div v-if="processedFavorites && processedFavorites.length"> <!--:class="{'-blur -transparent': loading}"-->
                    <div v-for="(favorite, i) in processedFavorites" :key="i">
                        <div class="favorites-album"
                             :class="{'favorites-album--single': !isMobile && processedFavorites.length === 1}">
                            <div class="favorites-album-title">
                                <router-link :to="i18nLink(`album/gallery/${getAlbumId(favorite, true)}`)">
                                    <span class="material-icon icon-undo"/>
                                    {{ favorite.albumTitle || $t('no_album_title') }}
                                </router-link>
                                <div class="favorites-album-actions">
                                    <router-link v-if="!isMobile && processedFavorites.length === 1" id="back-btn"
                                                 class="btn btn-light btn-icon-left text-uppercase"
                                                 :to="i18nLink(`album/gallery/${getAlbumId(favorite, true)}`)">
                                        <span class="material-icon icon-keyboard_arrow_left"/>
                                        {{ $t('back_to_album') }}
                                    </router-link>
                                    <a href="#" id="download-album-btn"
                                       @click.prevent="downloadPhotos(getDownloadPhotos(favorite))"
                                       v-if="getDownloadPhotos(favorite).length > 0"
                                       class="btn btn-light btn-icon-left mb-1 ml-3 text-uppercase">
                                        <span class="material-icon icon-cloud_download"/>
                                        {{ $tc('download_n_photos', favorite.items.length, {amount:
                                        getDownloadPhotos(favorite).length}) }}
                                    </a>
                                    <a href="#" id="order-selected-btn" v-if="+favorite.albumPricelist" @click.prevent="orderFavoritePhotos(favorite)"
                                       class="btn btn-success btn-icon-left btn-shadow mb-1 ml-2 text-uppercase">
                                        <span class="material-icon icon-library_add"/>
                                        {{ $tc('order_n_selected_photos', favorite.items.length, {amount:
                                        favorite.items.length}) }}
                                    </a>
                                </div>
                            </div>
                        </div>
                        <div class="favorites-images">
                            <isotope ref="isotope" :options='isotopeOptions' :list="favorite.items" v-images-loaded:on.progress="layout">
                                <div v-for="(img, key) in favorite.items" :key="key" class="favorites-images-item">
                                    <a href="#" @click.prevent="viewPhoto(getAlbumId(favorite), img.photoId, favorite)"
                                       :id="`photo-${img.photoId}`"
                                       class="favorites-images-item-box">
                                        <img :src="img.photoPath" ref="photo" alt="" @error="getNoPreview">
                                    </a>
                                    <div :id="`remove-action-${i}-${key}`"
                                         class="favorites-images-item-remove-icon"
                                         @click.prevent="deleteFromFavorites({ albumId: getAlbumId(favorite), albumStatus: favorite.albumStatus, photoId: img.photoId })">
                                        <span class="material-icon icon-indeterminate_check_box"></span>
                                    </div>
                                    <b-tooltip v-if="!isMobile" placement="top" :target="`remove-action-${i}-${key}`">
                                        {{ $t('helpers.remove_from_selection') }}
                                    </b-tooltip>
                                    <template v-if="+img.freeDownload">
                                        <div :id="`download-action-${i}-${key}`"
                                             class="favorites-images-item-download-icon"
                                             @click.prevent="downloadPhotos(img.photoId)">
                                            <span class="material-icon icon-cloud_download"></span>
                                        </div>
                                        <b-tooltip v-if="!isMobile" placement="top"
                                                   :target="`download-action-${i}-${key}`">
                                            {{ $t('helpers.download_photo') }}
                                        </b-tooltip>
                                    </template>
                                    <div class="favorites-images-item-order-icon">
                                        <span class="material-icon icon-add"/>
                                    </div>
                                    <div v-if="orderedPhotos[img.photoId]" class="favorites-images-item-labels">
                                        <div :id="`label-cart-${i}`" class="favorites-images-item-label">
                                            {{ $t('in_cart') }}
                                        </div>
                                        <b-tooltip v-if="!isMobile" placement="top" :target="`label-cart-${i}`">
                                            {{ $t('helpers.photo_in_cart_added') }}
                                        </b-tooltip>
                                    </div>
                                </div>
                            </isotope>
                        </div>
                    </div>
                </div>
                <div v-else>
                    <div class="alert alert-info">{{ $t('no_data') }}</div>
                </div>
            </div>
        </div>
        <powered-by v-if="hasPoweredBy"/>
    </div>
</template>

<style lang="scss">
    @import "../scss/variables";

    .favorites {
        margin-top: 60px;
        padding-bottom: 30px;

        .-blur {
            filter: blur(15px);
        }

        .-transparent {
            opacity: .1;
        }

        .row {
            margin: 0 -10px;

            [class*="col-"] {
                padding: 0 10px;
            }
        }

        &-album {
            margin-bottom: 35px;

            &-title {
                display: flex;
                align-items: center;
                justify-content: space-between;
                margin-bottom: 35px;
                font-size: 2.1rem;
                white-space: nowrap;
                overflow: hidden;

                > a {
                    max-width: 400px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;

                    &,
                    &:hover,
                    &:focus {
                        color: inherit;
                        text-decoration: none;
                    }
                }

                .material-icon {
                    margin-right: 10px;
                }
            }

            &--single {

                .favorites-album-actions {
                    position: fixed;
                    display: flex;
                    justify-content: space-between;
                    width: 100%;
                    padding: 0 41px;
                    height: 0;
                    left: 0;
                    bottom: 105px;
                    z-index: 1;

                    .btn {
                        height: 45px;
                        margin: 0 !important;
                        box-shadow: 0 24px 38px 3px rgba(#000, .14), 0 8px 46px 8px rgba(#000, .12), 0 11px 15px -7px rgba(#000, .2);
                    }

                    #back-btn {
                        order: 1;
                    }

                    #download-album-btn {
                        order: 3;
                    }

                    #order-selected-btn {
                        margin: 0 1rem 0 auto !important;
                        order: 2;
                    }
                }
            }
        }

        &-images {
            margin: 0 -10px;
            margin-bottom: 60px;

            &-item {
                position: relative;
                width: calc(100% / 5 - 20px);
                margin: 0 10px 20px;

                @media (max-width: $size-landscape-tablets) {
                    width: calc((100% - 80px) / 4);
                }

                @media (max-width: $size-landscape-phones) {
                    width: calc((100% - 60px) / 3);
                }

                @media (max-width: $size-portrait-phones) {
                    width: calc((100% - 40px) / 2);
                }

                &:not(.not-processed):hover {

                    .favorites-images-item {

                        /*&-download-icon,
                        &-remove-icon,*/
                        &-order-icon {
                            opacity: 1;
                        }

                        &-box {
                            box-shadow: 0 8px 10px 1px rgba(#000, .14), 0 3px 14px 2px rgba(#000, .14), 0 5px 5px -3px rgba(#000, .20);

                            &::before {
                                opacity: .5;
                            }
                        }
                    }
                }

                &-box {
                    position: relative;
                    display: block;
                    z-index: 0;

                    &::before {
                        position: absolute;
                        content: '';
                        width: 100%;
                        height: 100%;
                        top: 0;
                        left: 0;
                        opacity: 0;
                        transition: opacity .3s ease;
                        background-color: rgba(#000, .5);
                    }

                    > img {
                        width: 100%;
                        height: auto;
                    }
                }

                &-download-icon,
                &-remove-icon {
                    position: absolute;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 38px;
                    height: 38px;
                    top: 5px;
                    color: #fff;
                    text-shadow: 0 1px 3px rgba(#232323, .25);
                    cursor: pointer;
                    /*opacity: 0;*/
                    transition: opacity .3s ease;
                    z-index: 1;
                }

                &-remove-icon {
                    right: 3px;
                }

                &-download-icon {
                    right: 37px;
                }

                &-order-icon {
                    position: absolute;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 50px;
                    height: 50px;
                    top: 50%;
                    left: 50%;
                    color: #fff;
                    border-radius: 50%;
                    border: 2px solid;
                    transform: translate(-50%, -50%);
                    pointer-events: none;
                    opacity: 0;
                    transition: opacity .3s ease;
                    z-index: 1;
                }

                &-labels {
                    position: absolute;
                    top: 11px;
                    left: 8px;
                    cursor: pointer;
                }

                &-label {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    height: 22px;
                    margin-right: 10px;
                    padding: 0 10px;
                    font-size: .65rem;
                    font-weight: 500;
                    border-radius: 11px;
                    color: #212121;
                    background-color: #fafafa;
                    box-shadow: 0 2px 2px rgba(#000, .14), 0 3px 1px -2px rgba(#000, .12), 0 1px 5px rgba(#000, .2);

                    &:last-child {
                        margin-right: 0;
                    }
                }
            }
        }
    }

    body.-is-mobile {

        .favorites {
            margin-top: 40px;
            padding-bottom: 50px;

            &-album {
                margin-bottom: 30px;

                &-title {
                    flex-direction: column;
                    margin-bottom: 0;
                    font-size: 1.7rem;

                    .material-icon {
                        margin-right: 5px;
                        font-size: 1.3rem !important;
                    }

                    > a {
                        width: 100%;
                        text-align: center;
                    }
                }

                &-actions {
                    display: flex;
                    flex-direction: column;
                    margin-top: 10px;

                    .btn {
                        margin: 15px 0 0 !important;
                        font-size: .7rem;
                        justify-content: center;

                        &:first-child {
                            order: 2;
                        }

                        &:last-child {
                            order: 1;
                        }

                        .material-icon {
                            font-size: 1rem !important;
                        }
                    }
                }
            }

            &-images {
                margin-bottom: 30px;

                &-item {

                    &-download-icon,
                    &-order-icon {
                        display: none;
                    }

                    &-remove-icon {
                        opacity: 1;
                    }

                    &-box {

                        &::before {
                            content: none;
                        }
                    }

                    &:hover {

                        .favorites-images-item {

                            &-box {
                                box-shadow: none;
                            }
                        }
                    }
                }
            }
        }
    }

    .pe-none {
        pointer-events: none;
    }
</style>

<script>
  import Isotope from 'vueisotope';
  import imagesLoaded from 'vue-images-loaded';

  import Breadcrumbs from '../components/Breadcrumbs.vue';
  import PoweredBy from '../components/PoweredBy.vue';

  import favorites from 'src/mixins/favoritesPage';

  export default {
    directives: {
      imagesLoaded,
    },
    mixins: [favorites],
    components: {
      Breadcrumbs,
      Isotope,
      PoweredBy,
    },
    data() {
      return {
        loading: true,
        isotopeOptions: {
          itemSelector: '.grid-item',
          layoutMode: 'masonry',
        },
        processedFavorites: null,
      };
    },
    methods: {
      getAlbumId(favorite, tag = false) {
        const albumUrl = favorite.albumStatus === '2'
            ? `${favorite.albumHash}-${favorite.albumHashPassword}`
            : favorite.albumId;
        return `${albumUrl}${tag && favorite.tag && favorite.tag !== '' ? `/tag/${favorite.tag}` : ''}`;
      },
      loadAllImages() {
        const len = this.$refs.photo.length;
        let counter = 0;

        const incrementCounter = () => {
          counter++;
          if (counter === len) {
            setTimeout(() => {
              if (this.processedFavorites) {
                this.processedFavorites.forEach((favorite, index) => {
                  if (this.$refs.isotope && this.$refs.isotope[index]) {
                    this.$refs.isotope[index].layout('masonry');
                  }
                });
              }
              this.loading = false;
            });
          }
        };

        [].forEach.call(this.$refs.photo, function(img) {
          if (img.complete) {
            incrementCounter();
          } else {
            img.addEventListener('load', incrementCounter, false);
            img.addEventListener('error', incrementCounter, false);
          }
        });
      },
      getNoPreview(event) {
        event.target.src = require(`@/templates/${this.coreParams.theme_name}/assets/no-preview.jpg`);
        event.target.parentElement.classList.add('pe-none');
        event.target.parentElement.parentElement.classList.add('not-processed');
      },
      layout () {
        if (this.$refs.isotope) {
          this.$refs.isotope.forEach((item, index) => {
            if (this.$refs.isotope && this.$refs.isotope[index]) {
              this.$refs.isotope[index].layout('masonry');
            }
          });
        }
      },
    },
    watch: {
      '$route'() {
        if (this.$route.params) {
          this.processedFavorites = this.favorites.filter(
              favorite => this.getAlbumId(favorite) === this.$route.params.albumId);
        }
      },
      favorites(favorites) {
        if (favorites) {
          if (this.$route.params.albumId) {
            this.processedFavorites = favorites.filter(
                favorite => this.getAlbumId(favorite) === this.$route.params.albumId);
          } else {
            this.processedFavorites = favorites;
          }
        } else {
          this.processedFavorites = [];
        }
      },
      processedFavorites(processedFavorites) {
        const notProcessedElements = document.querySelector('.not-processed');
        const pointerEventsNoneElements = document.querySelector('.pe-none');

        if (notProcessedElements) {
          notProcessedElements.classList.remove('not-processed');
        }

        if (pointerEventsNoneElements) {
          pointerEventsNoneElements.classList.remove('pe-none');
        }

        if (processedFavorites && processedFavorites.length > 0) {
          // this.$nextTick(() => this.loadAllImages());
        }
      },
    },
    mounted() {
      if (this.favorites) {
        if (this.$route.params.albumId) {
          this.processedFavorites = this.favorites.filter(
              favorite => this.getAlbumId(favorite) === this.$route.params.albumId);
        } else {
          this.processedFavorites = this.favorites;
        }
      }

      if (this.processedFavorites && this.processedFavorites.length > 0) {
        this.$nextTick(() => this.loadAllImages());
      }
    },
  };
</script>
